import { memo, forwardRef } from "react";
import type { InputProps } from "../types";
import { colors, radius, spacing, transitions, typography } from "../tokens";

export const Input = memo(
  forwardRef<HTMLInputElement, InputProps>(function Input(
    { label, error, hint, prefix, suffix, id, style, ...props },
    ref
  ) {
    const inputId = id ?? `input-${Math.random().toString(36).slice(2, 7)}`;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
        {label && (
          <label
            htmlFor={inputId}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              fontWeight: typography.weight.medium,
              color: colors.textMuted,
            }}
          >
            {label}
          </label>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: spacing.sm,
            background: colors.surface,
            border: `1.5px solid ${error ? colors.error : colors.surfaceBorder}`,
            borderRadius: radius.md,
            padding: `${spacing.sm} ${spacing.md}`,
            transition: `border-color ${transitions.fast}`,
          }}
        >
          {prefix && (
            <span style={{ color: colors.textMuted, flexShrink: 0 }}>{prefix}</span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={!!error}
            aria-describedby={
              error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
            }
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: colors.text,
              fontFamily: typography.fontMono,
              fontSize: typography.size.sm,
              ...style,
            }}
            {...props}
          />
          {suffix && (
            <span style={{ color: colors.textMuted, flexShrink: 0 }}>{suffix}</span>
          )}
        </div>
        {error && (
          <span
            id={`${inputId}-error`}
            role="alert"
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.xs,
              color: colors.error,
            }}
          >
            {error}
          </span>
        )}
        {hint && !error && (
          <span
            id={`${inputId}-hint`}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.xs,
              color: colors.textMuted,
            }}
          >
            {hint}
          </span>
        )}
      </div>
    );
  })
);
